'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import AdSenseUnit from '@/components/AdSenseUnit';
import NavBar from '@/components/NavBar';

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  cover_image?: string;
  cover_image_alt?: string;
  category?: string;
  author?: string;
  is_featured?: boolean;
  views?: number;
  created_at?: string;
}

const PAGE_SIZE = 9;

export default function NewsPage() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const supabase = createClient();

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('news_posts')
          .select('*')
          .eq('is_published', true)
          .or('scheduled_at.is.null,scheduled_at.lte.' + new Date().toISOString())
          .order('created_at', { ascending: false });
        if (error) throw error;
        setPosts(data || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchQuery]);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(posts.map(p => p.category || 'News')))],
    [posts]
  );

  const featured = useMemo(
    () => posts.find(p => p.is_featured) || posts[0],
    [posts]
  );

  const filteredPosts = useMemo(() => {
    return posts.filter(p => {
      const matchesCategory = activeCategory === 'All' || (p.category || 'News') === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        (p.author || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [posts, activeCategory, searchQuery]);

  // Grid posts: exclude featured only when showing All with no search
  const gridPosts = useMemo(() => {
    const showingAll = activeCategory === 'All' && !searchQuery;
    return showingAll && featured ? filteredPosts.filter(p => p.id !== featured.id) : filteredPosts;
  }, [filteredPosts, featured, activeCategory, searchQuery]);

  const totalPages = Math.ceil(gridPosts.length / PAGE_SIZE);
  const paginatedPosts = gridPosts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const showFeaturedHero = activeCategory === 'All' && !searchQuery && !!featured;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-surface-primary font-condensed">
      {/* Header */}
      <NavBar
        activePage="news"
        hasTicker={false}
        rightSlot={
          <Link
            href="/"
            className="flex items-center gap-1.5 text-text-secondary hover:text-text-primary transition-colors text-sm font-bold uppercase tracking-wide min-h-[44px] px-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Back</span>
          </Link>
        }
      />

      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
        {/* Page Title + Search Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-stroke-primary pb-4 mb-6">
          <div className="flex items-baseline gap-3">
            <h1 className="text-text-primary text-3xl md:text-4xl font-normal">LATEST</h1>
            <span className="text-brand-red font-bold text-3xl md:text-4xl">NEWS</span>
          </div>
          {/* Search */}
          <div className="relative w-full sm:w-72">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-surface-secondary border border-stroke-primary text-text-primary text-sm placeholder-text-muted pl-9 pr-9 py-2 rounded-[4px] focus:outline-none focus:border-brand-red transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-surface-tertiary w-full rounded-[4px]" style={{ aspectRatio: '16/9' }} />
                <div className="mt-3 h-5 bg-surface-tertiary rounded w-3/4" />
                <div className="mt-2 h-4 bg-surface-tertiary rounded w-full" />
                <div className="mt-1 h-4 bg-surface-tertiary rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24">
            <svg className="w-16 h-16 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <p className="text-text-secondary text-xl mb-2">No news posts yet</p>
            <p className="text-text-muted text-sm">Check back soon for the latest updates.</p>
          </div>
        ) : (
          <>
            {/* Featured Post Hero */}
            {showFeaturedHero && (
              <Link
                href={`/news/${featured!.slug}`}
                className="mb-8 border border-stroke-primary group cursor-pointer hover:border-brand-red transition-colors duration-200 block"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative overflow-hidden bg-surface-tertiary" style={{ minHeight: '280px' }}>
                    {featured!.cover_image ? (
                      <img
                        src={featured!.cover_image}
                        alt={featured!.cover_image_alt || featured!.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-surface-tertiary">
                        <svg className="w-16 h-16 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="bg-brand-red text-white text-xs font-bold uppercase px-2 py-1">Featured</span>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center bg-surface-secondary">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-brand-red text-xs font-bold uppercase tracking-wide">{featured!.category || 'News'}</span>
                      <span className="text-text-muted text-xs">·</span>
                      <span className="text-text-muted text-xs">{formatDate(featured!.created_at)}</span>
                    </div>
                    <h2 className="text-text-primary text-2xl md:text-3xl font-bold leading-tight mb-3">{featured!.title}</h2>
                    {featured!.excerpt && (
                      <p className="text-text-secondary text-sm leading-relaxed mb-4 line-clamp-3">{featured!.excerpt}</p>
                    )}
                    <div className="flex items-center gap-3 text-text-muted text-xs">
                      <span>By {featured!.author || 'ViddStar'}</span>
                      {featured!.views !== undefined && (
                        <>
                          <span>·</span>
                          <span>{featured!.views.toLocaleString()} views</span>
                        </>
                      )}
                    </div>
                    <div className="mt-5">
                      <span className="inline-flex items-center gap-1.5 text-brand-red text-xs font-bold uppercase tracking-wide group-hover:gap-2.5 transition-all duration-150">
                        Read Article
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Category Filters */}
            {categories.length > 2 && (
              <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-[4px] border transition-all duration-150 ${
                      activeCategory === cat
                        ? 'bg-brand-red border-brand-red text-white' :'bg-transparent border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* AdSense — below category filters, above posts grid */}
            <AdSenseUnit
              adSlot="YYYYYYYYYY"
              adFormat="horizontal"
              className="mb-6"
              label="Advertisement"
            />

            {/* Search result count */}
            {searchQuery && (
              <p className="text-text-muted text-xs mb-4">
                {gridPosts.length === 0
                  ? 'No results found'
                  : `${gridPosts.length} result${gridPosts.length !== 1 ? 's' : ''} for "${searchQuery}"`}
              </p>
            )}

            {/* Posts Grid */}
            {paginatedPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedPosts.map(post => (
                  <Link
                    key={post.id}
                    href={`/news/${post.slug}`}
                    className="border border-stroke-primary group hover:border-brand-red transition-colors duration-200 cursor-pointer flex flex-col"
                  >
                    <div className="relative overflow-hidden bg-surface-tertiary" style={{ aspectRatio: '16/9' }}>
                      {post.cover_image ? (
                        <img
                          src={post.cover_image}
                          alt={post.cover_image_alt || post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-10 h-10 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <span className="bg-surface-secondary text-brand-red text-xs font-bold uppercase px-2 py-0.5 border border-brand-red">
                          {post.category || 'News'}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 flex flex-col flex-1 bg-surface-secondary">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-text-muted text-xs">{formatDate(post.created_at)}</span>
                        <span className="text-text-muted text-xs">·</span>
                        <span className="text-text-muted text-xs">By {post.author || 'ViddStar'}</span>
                      </div>
                      <h3 className="text-text-primary font-bold text-base leading-snug mb-2 group-hover:text-brand-red transition-colors duration-150 line-clamp-2">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-text-secondary text-sm leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-text-secondary text-lg">No posts found.</p>
                {(searchQuery || activeCategory !== 'All') && (
                  <button
                    onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                    className="mt-3 text-brand-red text-sm font-bold uppercase tracking-wide hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 mt-10">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-stroke-primary text-text-secondary text-xs font-bold uppercase tracking-wide rounded-[4px] hover:border-stroke-light hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                  aria-label="Previous page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  const isNear = Math.abs(page - currentPage) <= 1 || page === 1 || page === totalPages;
                  if (!isNear) {
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-1 text-text-muted text-xs">…</span>;
                    }
                    return null;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 text-xs font-bold rounded-[4px] border transition-all duration-150 ${
                        currentPage === page
                          ? 'bg-brand-red border-brand-red text-white' :'bg-transparent border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-stroke-primary text-text-secondary text-xs font-bold uppercase tracking-wide rounded-[4px] hover:border-stroke-light hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                  aria-label="Next page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            {/* Page indicator */}
            {totalPages > 1 && (
              <p className="text-center text-text-muted text-xs mt-3">
                Page {currentPage} of {totalPages} · {gridPosts.length} articles
              </p>
            )}

            {/* AdSense — below pagination */}
            <AdSenseUnit
              adSlot="ZZZZZZZZZZ"
              adFormat="auto"
              className="mt-8"
              label="Advertisement"
            />
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-stroke-primary bg-surface-secondary mt-12">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-text-muted text-xs">&copy; 2024 ViddStar. All rights reserved.</p>
          <Link href="/" className="text-text-muted hover:text-brand-red text-xs transition-colors">← Back to ViddStarTV</Link>
        </div>
      </footer>
    </div>
  );
}
